import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import { ModalHot } from 'components/common/ModalHot';
import { ButtonHot } from 'components/common/ButtonHot';
import { IconHot } from 'components/common/IconHot';
import { TooltipHot } from 'components/common/TooltipHot';
import { colors, spacing, radius } from 'common/theme';
import CopyIconSvg from 'assets/personal_zone/Copy.svg';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
const TAB_TYPES = {
  QRCODE: 'qrcode',
  BARCODE: 'barcode',
};

const isValidBarcodeValue = (value) => {
  if (!value || value.length === 0) return false;
  return /^[\x00-\x7F]+$/.test(value);
};

const TabButton = ({ label, isActive, onClick, testID }) => (
  <Box
    testId={testID}
    onClick={onClick}
    sx={{
      flex: 1,
      padding: `${spacing.sm}px ${spacing.md}px`,
      textAlign: 'center',
      cursor: 'pointer',
      borderRadius: `${radius.sm}px`,
      backgroundColor: isActive ? colors.system.brand.primary : 'transparent',
      color: isActive ? colors.palette.white : colors.system.text.subtle,
      fontWeight: isActive ? 'bold' : 'normal',
      transition: 'all 0.2s ease',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>
      {label}
    </Typography>
  </Box>
);

export const CouponCodeModal = ({
  open,
  onClose,
  couponCode,
  testID = 'coupon-code-modal',
}) => {
  const codeValue = String(couponCode || '') || ' ';
  const canShowBarcode = isValidBarcodeValue(couponCode);
  const [activeTab, setActiveTab] = useState(TAB_TYPES.QRCODE);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const { translate } = useTranslations();
  const handleCopyClick = async () => {
    try {
      await navigator.clipboard?.writeText(couponCode);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <ModalHot
      open={open}
      onClose={onClose}
      testID={testID}
      maxWidth={320}
    >
      <Box
        testId={generateTestId(testID, 'content')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${spacing.xl}px`,
          padding: `${spacing.md}px`,
        }}
      >
        <Box
          testId={generateTestId(testID, 'tabs')}
          sx={{
            display: 'flex',
            width: '100%',
            backgroundColor: colors.system.surface.subtle,
            borderRadius: `${radius.sm}px`,
            padding: `${spacing.xs}px`,
            gap: `${spacing.xs}px`,
          }}
        >
          <TabButton
            testID={generateTestId(testID, 'tab-qrcode')}
            label={translate('coupon_modal_switch_qr')}
            isActive={activeTab === TAB_TYPES.QRCODE}
            onClick={() => setActiveTab(TAB_TYPES.QRCODE)}
          />
          {canShowBarcode && (
            <TabButton
              testID={generateTestId(testID, 'tab-barcode')}
              label={translate('coupon_modal_switch_barcode')}
              isActive={activeTab === TAB_TYPES.BARCODE}
              onClick={() => setActiveTab(TAB_TYPES.BARCODE)}
            />
          )}
        </Box>

        {activeTab === TAB_TYPES.QRCODE && (
          <Box
            testId={generateTestId(testID, 'qrcode')}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: `${spacing.md}px`,
              backgroundColor: colors.palette.white,
              borderRadius: '8px',
            }}
          >
            <QRCode
              value={codeValue}
              size={150}
              style={{ height: 'auto', maxWidth: '100%', width: '150px' }}
              viewBox="0 0 256 256"
            />
          </Box>
        )}

        {activeTab === TAB_TYPES.BARCODE && canShowBarcode && (
          <Box
            testId={generateTestId(testID, 'barcode')}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: `${spacing.md}px`,
              backgroundColor: colors.palette.white,
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <Barcode
              value={codeValue}
              format="CODE128"
              height={80}
              width={1.5}
              displayValue={false}
            />
          </Box>
        )}

        <Box
          testId={generateTestId(testID, 'code-row')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: `${spacing.xs}px`,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.system.text.primary,
              fontWeight: 'bold',
              textAlign: 'center',
              direction: 'ltr',
            }}
          >
            {couponCode || ''}
          </Typography>
          <TooltipHot
            title={translate('coupon_code_copied')}
            open={copyTooltipOpen}
            disableHoverListener
            disableFocusListener
          >
            <IconButton
              testId={generateTestId(testID, 'copy-btn')}
              size="small"
              onClick={handleCopyClick}
              sx={{ color: colors.system.text.subtle, padding: `${spacing.xs}px` }}
            >
              <IconHot src={CopyIconSvg} size={20} />
            </IconButton>
          </TooltipHot>
        </Box>

        <ButtonHot
          testID={generateTestId(testID, 'close-btn')}
          onClick={onClose}
          variant="primary"
          size="medium"
        >
          {translate('benefit_page_close')}
        </ButtonHot>
      </Box>
    </ModalHot>
  );
};

export default CouponCodeModal;

